import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import filesize from 'filesize';
import * as Yup from 'yup';

import { ReactComponent as AlertIcon } from '../../../assets/icons/ubr_alert.svg';
import { Layout, Button, Input, Select } from '../../../components';
import SelectType from '../../../components/Form/Select/select_type';
import Upload from '../../../components/Upload';
import FileList from '../../../components/Upload/FileList';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { ManualData } from '../../../shared/interfaces/manual';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import {
  Container,
  FormContainer,
  ImportFileContainer,
  FooterContainer,
  Label,
  FileWrapper,
} from './styles';

interface FileProps {
  file: File;
  name: string;
  readableSize: string;
}

const Cadastro: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showErrorFile, setShowErrorFile] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileProps[] | any>([]);
  const [dataTable, setDataTable] = useState<SelectType[]>([]);
  let selectedScreens = useRef<number[] | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get<ScreenData[]>(`/tela`);

      let screenValues: SelectType[] = response.data.map(
        (screen: ScreenData) => {
          return {
            value: screen.id,
            label: screen.titulo,
          };
        },
      );

      setDataTable(screenValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function back() {
    if (location.pathname.match('/listar')) {
      history.push('/');
    } else {
      history.goBack();
    }
  }

  const handleSubmit = useCallback(
    async (data: ManualData) => {
      setIsLoading(true);
      const { file } = uploadedFiles[0];

      const telas = JSON.stringify(
        (data.listaDeTelas = selectedScreens.current),
      );

      const dataFile = new FormData();
      dataFile.append('file', file, file.name);
      dataFile.append('bucket', 'manual');
      dataFile.append('nomeManual', file.name.replace(/(.*)\.(.*?)$/, '$1'));
      dataFile.append('tituloArquivo', data.tituloArquivo);
      dataFile.append('listaDeTelas', telas.replace(/]|[[]|"/g, ''));

      try {
        if (!data.listaDeTelas?.length) {
          const schemaSelect = Yup.object().shape({
            tituloArquivo: Yup.string()
              .max(40, 'No máximo 40 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
            select: Yup.string().required('Este campo é obrigatório!'),
          });
          await schemaSelect.validate(data, {
            abortEarly: false,
          });
        }
        const schema = Yup.object().shape({
          tituloArquivo: Yup.string()
            .max(40, 'No máximo 40 caracteres')
            .matches(
              /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
              'Um ou mais caracteres digitados não são permitidos!',
            )
            .required('Este campo é obrigatório!'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const response = await api.post('/manual', dataFile, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response === undefined) {
          setIsLoading(false);
          addToast({
            type: 'error',
            title: 'Erro ao cadastrar o manual',
            description: `Houve um erro ao cadastrar o manual em nosso sistema!`,
            time: 6000,
          });
        }

        if (response.status === 200 || response.status === 201) {
          setIsLoading(false);
          addToast({
            type: 'success',
            title: 'Manual cadastrado com sucesso!',
            description:
              'O manual foi cadastrado. Você pode verificar o novo manual na seção de Listagem de Manuais.',
          });

          setTimeout(() => {
            history.push(RoutesPaths.listManual);
          }, 4000);
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          setIsLoading(false);
          return;
        }

        console.error(err);
      }
    },
    [uploadedFiles],
  );

  function handleScreenChange(dataTable: [] | null) {
    if (dataTable) {
      const valor = dataTable.map((item: any) => item['value']);
      selectedScreens.current = valor;
    }
  }

  function submitFile(files: File[]): void {
    const fileUpload = files.map(file => ({
      file,
      name: file.name,
      readableSize: filesize(file.size),
    }));
    setUploadedFiles(fileUpload);
    setShowErrorFile(true);
  }

  const removeFile = () => {
    setUploadedFiles('');
  };

  return (
    <>
      <Layout title={'Cadastro de Manuais'} hideFloatingButton>
        <Container>
          <FormContainer>
            <Form
              className="form-container"
              ref={formRef}
              onSubmit={handleSubmit}
            >
              <Input name="tituloArquivo" label="Nome do manual" type="text" />

              <Select
                name={'select'}
                options={dataTable}
                onChange={handleScreenChange}
                placeholder="Selecione"
                label="Telas"
                loading={!dataTable.length}
              />

              <ImportFileContainer>
                <Label>Arquivos</Label>
                <FileWrapper>
                  {!!uploadedFiles.length && (
                    <FileList files={uploadedFiles} handleErase={removeFile} />
                  )}
                </FileWrapper>
                <Upload onUpload={submitFile} accept={'application/pdf'} />
                <FooterContainer>
                  <p>
                    <AlertIcon />
                    Limite máximo de 5mb
                  </p>
                  <div>
                    <Button
                      variant="solid"
                      style={{ marginTop: '1rem' }}
                      type="submit"
                      disabled={!uploadedFiles.length}
                      loading={isLoading}
                      loadingMessage={'Enviando Manual...'}
                    >
                      Adicionar
                    </Button>
                    <Button
                      variant="outline"
                      color={'#C71F37'}
                      style={{ marginTop: '1rem' }}
                      onClick={() =>
                        setTimeout(() => {
                          back();
                        }, 500)
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                </FooterContainer>
              </ImportFileContainer>
            </Form>
          </FormContainer>
        </Container>
      </Layout>
    </>
  );
};

export default Cadastro;
